<template>
   <Head v-if="studyResp">
      <title>{{ $t("meta.study.title", { study_title: studyResp.title }) }}</title>
      <meta name="description" :content="$t('meta.study.content', { study_title: studyResp.title })" />
   </Head>

   <PageLoader :isLoading="isLoading" :loadErrors="loadErrors">
      <PageSections :contentSections="studyPageSections" />
   </PageLoader>

   <Modal
      v-if="showModal"
      id="study-page-locale"
      :title="studyLocaleModal.title"
      :pill="{ text: studyLocaleModal.pillText, theme: Theme.Sand }"
      @close="onModalClose"
      @confirm="onModalConfirm"
   >
      <p v-if="studyLocaleModal.text" class="text-center">{{ studyLocaleModal.text }}</p>

      <fieldset v-if="studyLocaleModal.showLocaleSelection" class="mt-4 rounded-lg border border-grey-10 p-6">
         <RadioInput
            v-for="locale in supportedStudyLocales"
            :id="`locale-${locale.id}`"
            :key="`locale-${locale.id}`"
            group="locales"
            :isSelected="locale.id === localeStore.currentLocale?.id"
            :label="locale.name"
            :value="locale.id"
            @option-selected="onLocaleSelection"
         />
      </fieldset>
   </Modal>
</template>

<script setup lang="ts">
import { Head } from "@unhead/vue/components";
import { useRoute } from "vue-router";
import { useLocaleStore } from "@/stores/localeStore";
import { useI18n } from "vue-i18n";
import StudiesAPI, { type StudyResp, type SupportedLanguageStudy } from "@/data/api-layer/studiesApi";
import { replaceLocaleInUrl, updateQueryParam } from "@/js/helpers/url.utils";
import { Theme } from "@/types";
import { useAppStore } from "@/stores/appStore";

interface UnsupportedLanguageTranslation {
   title: string;
   text?: string | null;
   tagText?: string | null;
}

interface StudyLocaleModal {
   title: string;
   text: string;
   pillText: string;
   showLocaleSelection: boolean;
}

const customSignupRedirect = inject<Ref<string | null>>("customSignupRedirect");

const appStore = useAppStore();
const $route = useRoute();
const i18n = useI18n();
const localeStore = useLocaleStore();
const isLoading = ref(true);
const loadErrors = ref<Error[]>([]);

const studyKey = $route.params.key as string;
const studyLocaleModal = ref<StudyLocaleModal>({
   title: "",
   text: "",
   pillText: "",
   showLocaleSelection: false,
});
const showModal = ref(false); // displays modal (on page load if locale is not supported)
const modalSelectedLocaleRadioId = ref<string | null>(null); // selected locale in the modal
const unsupportedLanguageTranslation = ref<UnsupportedLanguageTranslation | null>({ title: null }); // translations for modal, if study in this language doesn't exist
const studyResp = ref<StudyResp>(null);

// lifecycle
//-----------------------------------------------
onMounted(async () => {
   await fetchStudyContent();

   // If there is a prevLocale query param in the URL, the user tried to access the study page in a locale that was not supported
   // we need to show the modal with the supported locales (the prevLocale should be a valid site supported locale)
   if ($route.query.prevLocale && localeStore.siteSupportedLocales.find(({ id }) => id === $route.query.prevLocale)) {
      showLocaleNotSupportedModal($route.query.prevLocale);
   }

   customSignupRedirect.value = studyResp.value?.landingPage?.signupRedirect;
});

// Reset customSignupActive when leaving the page
onBeforeUnmount(() => {
   customSignupRedirect.value = null;
});

// computed
//-----------------------------------------------
const studyPageSections = computed(() => {
   if (!studyResp.value) return [];

   const studyPageSections = studyResp.value.landingPage.sections;
   return studyPageSections;
});

const supportedStudyLocales = computed(() => localeStore.pageSupportedLocales as SupportedLanguageStudy[]);

const primaryStudyLocale = computed(() => supportedStudyLocales.value.find((locale) => locale.isPrimary));

// finds study locale that matches current locale (in url)
const studyLocalesMatch = computed(() => supportedStudyLocales.value.find(({ id }) => id === i18n.locale.value));

// methods
//-----------------------------------------------
async function getPreviousLocaleSystemJson(locale) {
   try {
      // Import the JSON file based on the locale
      const messages = await import(`@/vue-i18n/locales/system/system.${locale}.json`);

      // Return the JSON content
      return messages.default;
   } catch (error) {
      console.error(`Failed to load messages for locale: ${locale}`, error);
      return null; // Handle the error or return a fallback value
   }
}

/**
 * Shows the modal with the supported locales for the study
 * Uses the prevLocale query param to show the message in the previously attemtped locale
 * @param prevLocale - the study locale that was attempted to be accessed
 */
async function showLocaleNotSupportedModal(prevLocale) {
   const showLocaleSelection = supportedStudyLocales.value.length > 1;
   modalSelectedLocaleRadioId.value = primaryStudyLocale.value.id; // make sure that the primary language is preselected

   // Load the JSON file for the given locale
   const prevLocaleJson = await getPreviousLocaleSystemJson(prevLocale);

   // default locale to show which we will overwrite if prevLocale is a valid locale
   const modalDynamicText = {
      title: i18n.t("system.redirected_to_locale.title", {
         primaryLocale: primaryStudyLocale.value.name,
         chosenLanguage: localeStore.siteSupportedLocales.find(({ id }) => id === i18n.locale.value)?.name,
      }),
      text: showLocaleSelection ? unsupportedLanguageTranslation.value.text : null,
      pillText: unsupportedLanguageTranslation.value.tagText,
   };

   // overwrite title, text, pillText if the prevLocale is valid
   if (prevLocaleJson) {
      modalDynamicText.title = prevLocaleJson.redirected_to_locale.title;
      modalDynamicText.title = modalDynamicText.title.replace("{primaryLocale}", primaryStudyLocale.value.name);
      modalDynamicText.title = modalDynamicText.title.replace(
         "{chosenLanguage}",
         localeStore.siteSupportedLocales.find(({ id }) => id === prevLocale)?.name
      );

      modalDynamicText.text = showLocaleSelection ? prevLocaleJson.redirected_to_locale.text : null;
      modalDynamicText.pillText = prevLocaleJson.redirected_to_locale.tag;
   }

   studyLocaleModal.value = {
      showLocaleSelection: showLocaleSelection,
      ...modalDynamicText,
   };

   showModal.value = true;
   // Now we can remove the prevLocale query param from the URL, as is no longer needed
   const currentUrl = new URL(window.location.href);
   currentUrl.searchParams.delete("prevLocale");
   // Update the URL without reloading the page
   window.history.replaceState(null, "", currentUrl.toString());
}

async function fetchStudyContent() {
   try {
      isLoading.value = true;

      showModal.value = false;

      // fetch the study supported locales and save them to the store
      await localeStore.setStudyLocales(studyKey);
      if (loadErrors.value.length > 0) return;

      if (localeStore.currentLocale) {
         modalSelectedLocaleRadioId.value = localeStore.currentLocale.id;
      }

      // if the current locale in the url is not one of the STUDY supported locales
      // redirect to the primary study locale and append the previous locale to the query params so we can show a modal
      if (!studyLocalesMatch.value) {
         const newLocaleinUrl: string = replaceLocaleInUrl($route.params.locale, primaryStudyLocale.value.id);
         const newUrl: string = updateQueryParam("prevLocale", i18n.locale.value, newLocaleinUrl);

         window.location.href = newUrl;
      }
      // locale is supported with this study
      else {
         // fetch the study details
         studyResp.value = await StudiesAPI.getOne(studyKey);
         // set the study_id in the visit query params (used to track the study page the user was interacting with)
         appStore.saveAnalyticsData(studyResp.value.id);
      }
   } catch (error) {
      loadErrors.value.push(error as Error);
   } finally {
      isLoading.value = false;
   }
}

function onLocaleSelection(id: string) {
   // dynamically update modalSelectedLocaleRadioId value, as user changes locales radio selection (before confirmation)
   modalSelectedLocaleRadioId.value = id;
}

function onModalClose() {
   showModal.value = false;
}

function onModalConfirm() {
   // this modal appears, when study page is not available in the target language
   // the study page changes route to the primary language first and then displays modal with available lanugages choice
   if (modalSelectedLocaleRadioId.value !== primaryStudyLocale.value.id) {
      // redirect to the study page with selected locale
      const newUrl = replaceLocaleInUrl($route.params.locale, modalSelectedLocaleRadioId.value);
      window.location.href = newUrl;
   } else {
      // The same locale is selected, so we just close the modal on confirm
      showModal.value = false;
   }
}
</script>
